import { mixColors } from '@avrahami/contracts';
import { flyerMonth, makerLine, splitTitle, textColorOn } from '../layout';
import {
  benefitIcon,
  bodyFont,
  cornerOf,
  displayFont,
  drawAmount,
  drawArrow,
  drawIcon,
  drawLines,
  drawLogo,
  drawPhoto,
  drawStruck,
  drawText,
  fillRect,
  fit,
  fitDescription,
  line,
  phoneOf,
  readable,
  strokeRect,
  W,
  type Page,
} from '../kit';
import {
  clipBody,
  drawMixedTitle,
  kickerOf,
  mixedTitleSize,
  savingOf,
  sizeRow,
  WORDS,
} from './shared';

/**
 * ATELIER: a page of a magazine. Paper inside a double hairline frame, a dated masthead, the
 * title in a heavy and a light weight, a numbered table of the specification with hairlines, the
 * photo as the issue's figure with a caption, and the price beside a dark call to action.
 */

const PAPER = '#f3eee5';
const LEFT = 84;
const RIGHT = 996;
const COLUMN = 440;
const TEXT_LEFT = RIGHT - COLUMN;
const RULE = 820;

export function drawAtelier(page: Page) {
  const { ctx, flyer } = page;
  const { theme, storeDesign } = flyer;
  const ink = storeDesign.ink;
  const muted = mixColors(ink, PAPER, 0.35);
  const hair = mixColors(ink, PAPER, 0.72);
  const brand = readable(theme.brand, PAPER, [theme.brandDark, ink]);
  const radius = cornerOf(storeDesign.corners, 10);

  fillRect(ctx, { x: 0, y: 0, w: W, h: W }, PAPER);
  strokeRect(ctx, { x: 36, y: 36, w: W - 72, h: W - 72 }, hair, 1.5);
  strokeRect(ctx, { x: 44, y: 44, w: W - 88, h: W - 88 }, hair, 1);

  // The masthead: the logo, and the issue's date and section.
  drawLogo(
    page,
    page.images.logo,
    flyer.store.name,
    { x: 600, y: 64, w: RIGHT - 600, h: 72 },
    {
      align: 'right',
      color: ink,
    },
  );
  drawText(ctx, flyerMonth(new Date()), LEFT, 94, {
    font: bodyFont(page, 600, 19),
    color: muted,
    align: 'left',
    spacing: 2,
  });
  const section = flyer.category === null ? 'מבחר העונה' : `מבחר העונה · ${flyer.category}`;
  drawText(ctx, clipBody(page, section, 700, 21, 420), LEFT, 124, {
    font: bodyFont(page, 700, 21),
    color: ink,
    align: 'left',
  });
  line(ctx, [44, 156], [W - 44, 156], hair, 1.5);

  // The kicker, with a rule running from it.
  const kicker = clipBody(page, kickerOf(flyer), 700, 19, COLUMN - 120);
  const kickerWidth = drawText(ctx, kicker, RIGHT, 200, {
    font: bodyFont(page, 700, 19),
    color: brand,
    spacing: 2,
  });
  line(ctx, [RIGHT - kickerWidth - 20, 193], [RIGHT - kickerWidth - 110, 193], brand, 1.5);

  // The title: its first part heavy, the rest light with its numbers heavy.
  const [head, rest] = splitTitle(flyer.title);
  const headline = fit(page, head, 'display', {
    maxWidth: COLUMN,
    maxLines: 2,
    maxSize: 100,
    minSize: 52,
    weight: 800,
  });
  let y = 218;
  y += drawLines(ctx, headline, {
    x: RIGHT,
    y,
    font: displayFont(page, headline.size),
    color: ink,
    lineHeight: 1.05,
  });
  if (rest !== '') {
    // One line when it fits at a good size, as a printed title sets it; otherwise two.
    const oneLine = mixedTitleSize(page, rest, {
      maxWidth: COLUMN,
      largest: headline.size,
      smallest: 60,
      maxLines: 1,
    });
    const size = fit(page, rest, 'display', {
      maxWidth: COLUMN,
      maxLines: 1,
      maxSize: oneLine,
      minSize: oneLine,
      weight: 800,
    }).lines.some((text) => text.endsWith('…'))
      ? mixedTitleSize(page, rest, {
          maxWidth: COLUMN,
          largest: headline.size,
          smallest: 40,
          maxLines: 2,
        })
      : oneLine;
    y += drawMixedTitle(page, rest, {
      right: RIGHT,
      top: y,
      size,
      maxWidth: COLUMN,
      color: ink,
      lightWeight: 300,
    });
  }

  // The specification, numbered, with hairlines; the strengths when it has no rows.
  const rows: { label: string; value: string; icon?: Parameters<typeof benefitIcon>[0] }[] =
    flyer.specs.length > 0
      ? flyer.specs.slice(0, 4)
      : flyer.benefits
          .slice(0, 4)
          .map((benefit) => ({ label: benefit.title, value: '', icon: benefit.icon }));
  const rowHeight = 60;
  const rowsTop = RULE - rows.length * rowHeight;

  const descriptionTop = y + 30;
  const room = rowsTop - 22 - descriptionTop;
  // Whole sentences only: a sentence cut in the middle is not drawn (fitDescription).
  const text =
    room > 34
      ? fitDescription(page, flyer.description, {
          maxWidth: COLUMN,
          maxLines: Math.max(1, Math.floor(room / (24 * 1.6))),
          maxSize: 25,
          minSize: 21,
          weight: 400,
        })
      : null;
  if (text !== null) {
    drawLines(ctx, text, {
      x: RIGHT,
      y: descriptionTop,
      font: bodyFont(page, 400, text.size),
      color: muted,
      lineHeight: 1.6,
    });
  }

  rows.forEach((row, index) => {
    const top = rowsTop + index * rowHeight;
    const middle = top + rowHeight / 2;
    line(ctx, [TEXT_LEFT, top], [RIGHT, top], hair, 1);
    if (row.icon === undefined) {
      drawText(ctx, String(index + 1).padStart(2, '0'), RIGHT, middle + 7, {
        font: bodyFont(page, 700, 17),
        color: brand,
        direction: 'ltr',
        spacing: 1,
      });
    } else {
      drawIcon(ctx, benefitIcon(row.icon), RIGHT - 12, middle, 24, brand);
    }
    ctx.font = bodyFont(page, 400, 23);
    const valueWidth =
      row.value === '' ? 0 : Math.min(ctx.measureText(row.value).width, COLUMN * 0.45);
    drawText(
      ctx,
      clipBody(page, row.label, 700, 23, COLUMN - 70 - valueWidth - 16),
      RIGHT - 70,
      middle + 8,
      {
        font: bodyFont(page, 700, 23),
        color: ink,
      },
    );
    if (row.value !== '') {
      drawText(ctx, clipBody(page, row.value, 400, 23, valueWidth), TEXT_LEFT, middle + 8, {
        font: bodyFont(page, 400, 23),
        color: muted,
        align: 'left',
      });
    }
  });

  // The figure: the photo on a darker paper, its number and its caption.
  const figure = { x: LEFT, y: 200, w: COLUMN, h: 560 };
  const ground = mixColors(ink, PAPER, 0.91);
  fillRect(ctx, figure, ground, radius);
  drawPhoto(page, { x: LEFT + 24, y: 250, w: COLUMN - 48, h: 480 }, radius);
  drawText(ctx, 'FIG. 1', LEFT + COLUMN - 20, 232, {
    font: bodyFont(page, 700, 15),
    color: brand,
    direction: 'ltr',
    spacing: 3,
  });
  const maker = makerLine(flyer.maker);
  drawText(
    ctx,
    clipBody(page, maker === '' ? `${WORDS.sku} ${flyer.sku}` : maker, 600, 16, 230),
    LEFT + COLUMN,
    792,
    {
      font: bodyFont(page, 600, 16),
      color: muted,
      spacing: 1.5,
    },
  );
  const size = sizeRow(flyer);
  if (size !== null) {
    drawText(ctx, clipBody(page, size, 600, 16, 190), LEFT, 792, {
      font: bodyFont(page, 600, 16),
      color: muted,
      align: 'left',
    });
  }
  line(ctx, [LEFT, RULE], [RIGHT, RULE], mixColors(ink, PAPER, 0.55), 1.5);

  // The price, and what the sale saves.
  const { price } = flyer;
  const saving = savingOf(flyer);
  if (price !== null) {
    drawText(ctx, saving === null ? WORDS.price : WORDS.salePrice, RIGHT, 860, {
      font: bodyFont(page, 600, 18),
      color: muted,
    });
    const width = drawAmount(page, price.amountMinor, {
      x: RIGHT,
      baseline: 932,
      size: 78,
      color: ink,
      maxWidth: 330,
    });
    if (saving !== null && price.compareAtMinor !== null) {
      const instead = drawText(ctx, WORDS.instead, RIGHT, 966, {
        font: bodyFont(page, 400, 20),
        color: muted,
      });
      drawStruck(page, price.compareAtMinor, {
        x: RIGHT - instead - 8,
        baseline: 966,
        font: bodyFont(page, 400, 20),
        color: muted,
        strike: brand,
      });
      const divider = RIGHT - Math.max(width, 240) - 34;
      line(ctx, [divider, 864], [divider, 950], hair, 1.5);
      drawAmount(page, saving, {
        x: divider - 30,
        baseline: 918,
        size: 46,
        color: brand,
        maxWidth: 170,
      });
      drawText(ctx, WORDS.saving, divider - 30, 948, {
        font: bodyFont(page, 400, 18),
        color: muted,
      });
    }
  } else {
    drawText(ctx, WORDS.quote, RIGHT, 916, {
      font: displayFont(page, 46),
      color: ink,
      maxWidth: RIGHT - LEFT - 420 - 40,
    });
    drawText(ctx, WORDS.noCommitment, RIGHT, 954, { font: bodyFont(page, 400, 20), color: muted });
  }

  // The call to action: a dark plate with the phone and an arrow.
  const plate = { x: LEFT, y: 862, w: 420, h: 72 };
  fillRect(ctx, plate, ink, radius);
  const onInk = textColorOn(ink);
  const ctaWidth = drawText(ctx, WORDS.cta, plate.x + plate.w - 22, plate.y + 45, {
    font: bodyFont(page, 700, 23),
    color: onInk,
  });
  const phone = phoneOf(flyer);
  const divider = plate.x + plate.w - 22 - ctaWidth - 16;
  if (phone !== null) {
    line(ctx, [divider, plate.y + 20], [divider, plate.y + 52], mixColors(onInk, ink, 0.6), 1.5);
    drawText(
      ctx,
      clipBody(page, phone, 600, 21, divider - plate.x - 64),
      divider - 14,
      plate.y + 45,
      {
        font: bodyFont(page, 600, 21),
        color: onInk,
        direction: 'ltr',
      },
    );
  }
  drawArrow(
    ctx,
    plate.x + 34,
    plate.y + plate.h / 2,
    26,
    readable(theme.accent, ink, [theme.brand, onInk]),
  );
}
